import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { useVitalSigns } from '../context/VitalSignsContext';

const ResultsContainer = styled.div`
  padding: 2rem 0px;
  max-width: 800px;
  margin: 0 auto;
`;

const ResultCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 1rem;
`;

const ResultTitle = styled.h2`
  color: #333;
  margin-bottom: 1.5rem;
`;

const ResultItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid #eee;

  &:last-child {
    border-bottom: none;
  }
`;

const Label = styled.span`
  font-weight: 500;
  color: #666;
`;

const Value = styled.span`
  color: #333;
  font-weight: 600;
`;

const Confidence = styled.span`
  color: #666;
  font-size: 0.9em;
  margin-left: 8px;
`;

interface VitalSignValue {
  value: number;
  confidenceLevel?: number;
}

const isVitalSignValue = (value: any): value is VitalSignValue => {
  return value && typeof value === 'object' && 'value' in value;
};

const formatValue = (value: any, unit: string = '') => {
  if (isVitalSignValue(value)) {
    return (
      <>
        <Value>
          {value.value}
          {unit}
        </Value>
        {value.confidenceLevel && (
          <Confidence>(Confianza: {(value.confidenceLevel * 33).toFixed(1)}%)</Confidence>
        )}
      </>
    );
  }
  return <Value>{value}{unit}</Value>;
};

const formatStressLevel = (stressLevel : any) => {

  let displayString: string;

  switch (stressLevel.value) {
    case 0:
      displayString = "Desconocido";
      break; // Important: don't forget break;
    case 1:
      displayString = "Bajo";
      break;
    case 2:
      displayString = "Normal";
      break;
    case 3:
      displayString = "Medio";
      break;
    case 4:
      displayString = "Alto";
      break;
    case 4:
      displayString = "Extremo";
      break;

    default: // This acts like the 'else' or 'catch-all'
      displayString = "Valor fuera de rango";
      break;
  }
  return <Value>{displayString}</Value>;
};

const ResultsPage: React.FC = () => {
  const history = useHistory();
  const { vitalSignsResults } = useVitalSigns();

  useEffect(() => {
    if (!vitalSignsResults) {
      history.push('/');
    }
  }, [vitalSignsResults, history]);

  if (!vitalSignsResults) {
    return null;
  }

  const bp = vitalSignsResults.bloodPressure?.value;
  const hasBP = bp?.systolic && bp?.diastolic;

  return (
    <ResultsContainer>
      <ResultTitle>Resultados de Signos Vitales</ResultTitle>
      <ResultCard>
        {hasBP && (
          <>
            <ResultItem>
              <Label>Presión Arterial Sistólica:</Label>
              <Value>{bp.systolic} mmHg</Value>
              {vitalSignsResults.bloodPressure.confidenceLevel && (
                <Confidence>(Confianza: {(vitalSignsResults.bloodPressure.confidenceLevel * 33).toFixed(1)}%)</Confidence>
              )}
            </ResultItem>
            <ResultItem>
              <Label>Presión Arterial Diastólica:</Label>
              <Value>{bp.diastolic} mmHg</Value>
              {vitalSignsResults.bloodPressure.confidenceLevel && (
                <Confidence>(Confianza: {(vitalSignsResults.bloodPressure.confidenceLevel * 33).toFixed(1)}%)</Confidence>
              )}
            </ResultItem>
          </>
        )}
        {vitalSignsResults.respirationRate && (
          <ResultItem>
            <Label>Frecuencia Respiratoria:</Label>
            {formatValue(vitalSignsResults.respirationRate, ' resp/min')}
          </ResultItem>
        )}
        {vitalSignsResults.respirationRate && (
          <ResultItem>
            <Label>Ritmo Cardíaco:</Label>
            {formatValue(vitalSignsResults.pulseRate.value, ' lat/min')}

            {vitalSignsResults.pulseRate.confidenceLevel && (
              <Confidence>(Confianza: {(vitalSignsResults.pulseRate.confidenceLevel * 33).toFixed(1)}%)</Confidence>
            )}
          </ResultItem>
        )}
        {vitalSignsResults.oxygenSaturation && (
          <ResultItem>
            <Label>Saturación de Oxígeno:</Label>
            {formatValue(vitalSignsResults.oxygenSaturation, '%')}
          </ResultItem>
        )}
        {vitalSignsResults.stressLevel && (
          <ResultItem>
            <Label>Nivel de Estrés:</Label>
            {formatStressLevel(vitalSignsResults.stressLevel)}
          </ResultItem>
        )}
      </ResultCard>
    </ResultsContainer>
  );
};

export default ResultsPage;
